import { Localized } from "@fluent/react";
import {
    Dialog, DialogActions, DialogContent, DialogTitle, TextField,
    Box,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Alert,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { Device, updateDeviceDescription } from "../deviceSlice";
import { useAppDispatch } from "../../../app/hooks";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FieldKey } from "../controls/KeyValueRow";
import { StructureTreeSelect } from "../../structure/StructureTreeSelect";
import { SuppliersSelector } from "../../suppliers/components/SuppliersSelector";
import { Help } from "../../dialogs/HelpDialog";

export interface EditDeviceFieldProps {
    device?: Device;
    field: Exclude<FieldKey, "serialNo">;
    value: ReactNode;
    onSave: (device: Device) => void;
    onCancel: () => void;
}

type FieldType = "string" | "number" | "checkbox" | "year" | "structure" | "supplierSelector";

type FieldMeta = {
    type: FieldType;
    label?: React.ReactNode;
    options?: Array<{ id: number | string; name: string }>;
    placeholder?: string;
};

const FIELD_META: Record<string, FieldMeta> = {
    name: { type: "string", label: <Localized id="devices-name">_Nazwa urządzenia</Localized> },
    structureId: { type: "structure", label: <Localized id="devices-type">_Typ</Localized> },
    manufacturer: { type: "string", label: <Localized id="devices-producer">_Producent</Localized> },
    model: { type: "string", label: <Localized id="devices-model">_Model</Localized> },
    productionYear: { type: "year", label: <Localized id="devices-production-year">_Rok produkcji</Localized> },
    description: { type: "string", label: <Localized id="devices-description">_Opis</Localized> },
    supplier: { type: "supplierSelector", label: <Localized id="devices-supplier">_Dostawca</Localized> },
    isGxpRelevant: { type: "checkbox", label: "GxP" },
};

export const EditDeviceField = (props: EditDeviceFieldProps) => {
    const dispatch = useAppDispatch();
    const { field, value, device } = props;

    const getInitialValue = () => {
        if (!device) return value ?? "";

        if (field === "structureId") return device.structureId ?? "";
        if (field === "isGxpRelevant") return device.isGxpRelevant ?? false;

        if (typeof value === "object" && value !== null) return "";

        return value ?? "";
    };

    const [newValue, setNewValue] = useState<any>(getInitialValue());
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<string>("");

    const renderEditor = () => {
        const meta = FIELD_META[field as string];
        if (meta) {
            switch (meta.type) {
                case "string":
                    return (
                        <TextField
                            sx={{ mt: 1 }}
                            fullWidth
                            size="small"
                            label={meta.label ?? <>{field}</>}
                            value={String(newValue ?? "")}
                            onChange={(e) => setNewValue(e.target.value)}
                        />
                    );

                case "year":
                    return (<TextField
                        sx={{ mt: 1 }}
                        fullWidth
                        size="small"
                        label={meta.label ?? <>{field}</>}
                        type="number"
                        value={newValue === null || newValue === undefined || newValue === "" ? "" : String(newValue)}
                        onChange={(e) => {
                            const v = e.target.value;
                            setNewValue(v === "" ? "" : Number(v));
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarMonthIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                </InputAdornment>
                            ),
                        }}
                    />);

                case "structure":
                    return (
                        <StructureTreeSelect defaultStructureId={newValue} onStructureSelected={(id) => setNewValue(id)} />
                    );

                case "supplierSelector":
                    return (
                        <SuppliersSelector
                            selectedStructureId={props.device?.structureId ?? null}
                            showFiltersTab
                            filterByStructure={props.device?.structureId != null}
                            dialogOnly
                            value={newValue ?? ""}
                            open={true}
                            onSubmit={(response) => {
                                setNewValue(response?.supplierId ?? "")
                                handleOkClick(response?.supplierId ?? "")
                            }}
                            onCancel={() => props.onCancel()}
                        />
                    )
                case "checkbox":
                    return (
                        <Box
                            sx={{
                                p: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: "grey.50",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                    <Checkbox
                                        checked={newValue === true}
                                        onChange={(e) => setNewValue(e.target.checked)}
                                    />
                                }
                                label={
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                        <Localized id="cs-is-gxp">
                                            <span>System GxP</span>
                                        </Localized>
                                    </span>
                                }
                            />

                            <Help id="71f8633a-6475-4d19-b674-f7bc2fc391ec" />
                        </Box>
                    );

                default:
                    return (
                        <TextField
                            sx={{ mt: 1 }}
                            fullWidth
                            multiline
                            minRows={3}
                            size="small"
                            label={meta.label ?? <>{field}</>}
                            value={typeof newValue === "string" ? newValue : JSON.stringify(newValue ?? "")}
                            onChange={(e) => setNewValue(e.target.value)}
                        />
                    );
            }
        }

        return (
            <TextField
                sx={{ mt: 1 }}
                fullWidth
                multiline
                minRows={3}
                size="small"
                label={<>{field}</>}
                value={typeof newValue === "string" ? newValue : JSON.stringify(newValue ?? "")}
                onChange={(e) => setNewValue(e.target.value)}
            />
        );
    };

    const handleOkClick = async (directValue?: any) => {
        setSaving(true);
        setServerError("");
        try {
            const valueToSave = directValue !== undefined ? directValue : newValue;
            const payloadText = valueToSave == null || valueToSave === "" 
            ? "" 
            : typeof valueToSave === "object" 
                ? JSON.stringify(valueToSave) 
                : String(valueToSave);

            const result = await dispatch(updateDeviceDescription({
                deviceId: Number(device?.id),
                data: { fieldName: field, text: payloadText }
            }));

            if (updateDeviceDescription.fulfilled.match(result)) {
                props.onSave(result.payload as Device);
            } else if (updateDeviceDescription.rejected.match(result)) {
                const error = result.payload as any;
                const errorMessage = error?.problem?.detail || error?.problem?.title || "Validation error occurred";
                setServerError(errorMessage);
            }
        } catch (e: any) {
            const msg = e?.payload?.problem?.title ?? e?.message ?? "Error occurred during saving.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm">
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[200],
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.75,
                }}>
                <Localized id="edit">Edycja</Localized>
            </DialogTitle>

            <DialogContent sx={{ pt: '10px !important' }}>
                {renderEditor()}
                {serverError &&
                    <Alert sx={{ mt: 2 }} severity="error">{serverError}</Alert>}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={() => handleOkClick()} />
                <CancelButton loading={saving} onClick={props.onCancel} />
            </DialogActions>
        </Dialog>
    );
};
